import { useState } from 'react';
import { AppButton, AppFlex, AppText, AppTitle, DetailsFrameBase } from 'components/atoms';
import { useAppQuery } from 'services/reactQuery/useAppQuery';

import ParentHTTPService from 'services/HTTPService/parent/ParentHTTPService';
import { IconDownload, IconFileInvoice } from '@tabler/icons-react';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import { downloadFileFromHex } from 'utils/fileDownload';
import { settlementTexts } from 'consts/text/index';
import { Row, Col, Card } from 'antd';
import styled from 'styled-components';
import { ChildId } from 'types';

const ItemContainer = styled.div`
    margin-block-start: 32px;
    margin-top: 32px;
`;

type ParentInvoicesProps = {
    period: string;
    childId: ChildId;
};

export const ParentInvoices = ({ period, childId }: ParentInvoicesProps) => {
    const { displayError } = useSimpleNotification();
    const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<string | null>(null);

    const { data: childSettlement, isLoading } = useAppQuery(
        'CHILD_SETTLEMENT',
        [period, childId],
        () =>
            ParentHTTPService.settlements.getChildSettlement({
                period,
            }),
        {},
    );

    const handleDownloadInvoice = async (invoiceId: string, fileName: string) => {
        try {
            setDownloadingInvoiceId(invoiceId);
            const response = await ParentHTTPService.invoices.printInvoice({
                invoiceId,
                childId,
            });

            // The response is configured as arraybuffer, but may come as hex string
            // Convert the response data to hex string if it's not already
            const hexData =
                typeof response.data === 'string'
                    ? response.data
                    : Array.from(new Uint8Array(response.data))
                          .map((byte) => byte.toString(16).padStart(2, '0'))
                          .join('');

            downloadFileFromHex(hexData, `${fileName}.pdf`);
        } catch (error) {
            displayError(settlementTexts.children.invoices.downloadError);
            console.error('Error downloading invoice:', error);
        } finally {
            setDownloadingInvoiceId(null);
        }
    };

    if (isLoading) {
        return (
            <ItemContainer>
                <DetailsFrameBase>
                    <AppText>Ładowanie rozliczeń...</AppText>
                </DetailsFrameBase>
            </ItemContainer>
        );
    }
    const invoice = childSettlement?.monthlySettlementDetailsDto.invoiceData;

    if (!invoice) {
        return null;
    }

    return (
        <ItemContainer>
            <AppTitle level={4} style={{ marginBottom: 16 }}>
                <IconFileInvoice style={{ marginRight: 8 }} />
                Rozliczenia
            </AppTitle>

            <Row gutter={[16, 16]}>
                <Col xs={24} key={invoice.id}>
                    <Card
                        title={
                            <AppFlex align="center" gap="small">
                                <IconFileInvoice size={16} />
                                <AppText>Rozliczenie główne</AppText>
                            </AppFlex>
                        }
                        extra={
                            <AppButton
                                type="link"
                                size="small"
                                icon={<IconDownload size={16} />}
                                loading={downloadingInvoiceId === invoice.id}
                                onClick={() =>
                                    handleDownloadInvoice(invoice.id, invoice.invoiceNumber)
                                }
                            >
                                Pobierz
                            </AppButton>
                        }
                    >
                        <AppText textType="BodySmall" color="secondary">
                            {invoice.invoiceNumber}
                        </AppText>
                        <br />
                        <AppText textType="BodySmall" color="secondary">
                            Utworzono: {new Date(invoice.issueDate).toLocaleDateString('pl-PL')}
                        </AppText>
                    </Card>
                </Col>
            </Row>
        </ItemContainer>
    );
};
